import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Chip, DChip, DataTable, EmptyState, Icon, PhoneCards, PhoneScreen, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { captureFile, useMyDay, useTaskActions, type NurseTask } from './api';
import { allergenOf } from '@/utils/allergyText';
import { buildItems, dayRows, LANE_CHIP, type DayFilter, type DayItem } from './dayModel';
import {
  cantLabel, clock, drName, mmss, numberWord, secondsLeft, shortName, sinceWhen, taskLabel, useNow,
  execTitle,
} from './format';
import { TaskPane } from './components/TaskPane';
import { CantModal, DayCantSub } from './components/CantModal';
import { FlagLowModal } from './components/NurseModal';
import { NurseSearch } from './components/NurseSearch';
import { NurseMenu } from './components/NurseMenu';

/* The widths the board RENDERED (138 / 311 / 133 / 206 of 788px), pinned with
   fixed layout (nurse.css `.nurse-fixed`) so the columns never move with the
   rows' text. */
const COLS = [
  { key: 'lane', label: 'Lane', width: 17.51 },
  { key: 'task', label: 'Task', width: 39.47 },
  { key: 'ctx', label: 'Context', width: 16.88 },
  { key: 'state', label: 'State', width: 26.14 },
];
/* A desk table shows ten, then states the rest: "showing 10 of 13 · Load more". */
const PAGE = 10;

const isOpen = (t: NurseTask) => t.state === 'open' || t.state === 'in_progress';

/**
 * Nurse · My day — `maie-desktop-nurse/g_day` and `g_empty`.
 *
 * THE THREE LANES AS ONE TABLE. From the rooms · Prep · After the visit are
 * one list, not three tabs: the lane is a column, and its chip sits on the
 * first row of each lane only. A room's running work (a dilation on its clock,
 * two now-tasks a room sent out together) is tinted beige under the lane.
 *
 * Done rows are hidden until she asks for them ("All"), and "Handed over" is
 * what left with a hand-over today — its zero state walks her into one.
 */
const LANE_HEAD: Record<DayItem['lane'], string> = {
  rooms: 'From the rooms — now',
  prep: 'Prep — before the doctor',
  after: 'After the visit — from signed plans',
};

export function NurseDayPage() {
  const navigate = useNavigate();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const day = useMyDay();
  const act = useTaskActions();
  const [filter, setFilterState] = useState<DayFilter>('open');
  const [shown, setShown] = useState(PAGE);
  /* A new lane filter starts at the first page — but never pages away the
     row that is docked: the pane is an extension of the rows on screen. */
  const setFilter = (f: DayFilter) => {
    setFilterState(f);
    const at = selectedId ? buildItems(day.data?.tasks ?? [], f).findIndex((i) => i.id === selectedId) : -1;
    setShown(Math.max(PAGE, at + 1));
  };
  const [searching, setSearching] = useState(false);
  /* A row picked in the search overlay on another screen arrives docked. */
  const location = useLocation();
  const wanted = (location.state as { select?: string } | null)?.select ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(wanted);
  const [cantTask, setCantTask] = useState<NurseTask | null>(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const fileTask = useRef<string | null>(null);
  usePageMeta({ title: 'My day' });

  const tasks = useMemo(() => day.data?.tasks ?? [], [day.data]);
  /* Counted in ROWS, the way each list draws them — a room's two now-tasks are
     one row — so a chip can never disagree with the list it filters. */
  const counts = {
    open: buildItems(tasks, 'open').length,
    handedOver: buildItems(tasks, 'handed').length,
    all: buildItems(tasks, 'all').length,
  };
  const rooms = day.data?.rooms ?? [];
  const running = tasks.some((t) => t.state === 'in_progress' && t.dueAt);
  const now = useNow(running);

  const items = useMemo(() => dayRows(tasks, filter, ''), [tasks, filter]);
  const loadedDay = Boolean(day.data);
  /* Dock a row picked in the search overlay: a done or handed-over row is not
     in Open, so show the list it is in, paged far enough to include it. */
  const dock = (id: string) => {
    setSelectedId(id);
    const inOpen = buildItems(tasks, 'open').findIndex((i) => i.id === id);
    if (inOpen >= 0 && filter === 'open') {
      setShown((n) => Math.max(n, inOpen + 1));
      return;
    }
    const f: DayFilter = inOpen >= 0 ? 'open' : 'all';
    const at = buildItems(tasks, f).findIndex((i) => i.id === id);
    setFilterState(f);
    setShown(Math.max(PAGE, at + 1));
  };
  useEffect(() => {
    if (!wanted || !loadedDay) return;
    dock(wanted);
    navigate('.', { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, loadedDay]);

  const selected: DayItem | null = items.find((i) => i.id === selectedId) ?? items[0] ?? null;
  const fail = (e: unknown) => toast.error((e as Error).message || 'That did not go through');

  const scan = (id: string) => {
    fileTask.current = id;
    fileRef.current?.click();
  };
  const onFile = async (file: File | undefined) => {
    const id = fileTask.current;
    if (!file || !id) return;
    try {
      await captureFile(id, file);
      toast.success('Signed consent filed — the order is unblocked');
      void day.refetch();
    } catch (e) {
      fail(e);
    }
  };

  const roomsLine = rooms.length
    ? rooms.map((r) => `${drName(r)} #${r.number ?? '—'}`).join(' · ')
    : null;


  let lastLane: string | null = null;
  /* Every row is built; the desk table pages them, the phone list does not —
     it scrolls, and has no "Load more" to reach a twelfth task with. */
  const allRows: TableRow[] = items.map((i) => {
    const t = i.primary;
    const showLane = i.lane !== lastLane;
    lastLane = i.lane;
    const lane = LANE_CHIP[i.lane];
    const left = secondsLeft(t, now);
    const req = drName(t.requestedBy);
    const closed = !i.tasks.some(isOpen);

    /* THE TASK CELL — title, the chips that belong to it, and one quiet line. */
    const title = i.batch
      ? `${shortName(t.patient)} — ${i.tasks.length} now-tasks`
      : t.kind === 'dilate' && t.state === 'in_progress'
        ? `${shortName(t.patient)} — dilating`
        : taskLabel(t);
    const chips = (
      <>
        {t.kind === 'vitals'
          ? (t.patient?.allergies ?? []).map((a) => (
            <Chip key={a} kind="danger" small title={a}>{allergenOf(a) || a}</Chip>
          ))
          : null}
        {t.doseNo && t.doseTotal ? <Chip kind="outline" small>dose {t.doseNo} of {t.doseTotal}</Chip> : null}
        {t.nurseOnly ? <Chip kind="warn" small>nurse-only visit</Chip> : null}
      </>
    );
    const quiet: string[] = [];
    if (i.batch) {
      quiet.push(i.tasks.map((s) =>
        `${s.kind === 'other' ? s.title : s.kind}${s.state === 'done' ? ' ✓' : s.state === 'in_progress' ? ' in progress' : s.state === 'cant' ? ' — can’t' : ''}`,
      ).join(' · '));
    } else if (t.kind === 'dilate' && t.state === 'in_progress') {
      quiet.push(`back to ${req || 'the doctor'} at 0`);
    } else if (t.kind === 'chaperone' && req) {
      quiet.push(`${req} requested`);
    }
    if (t.detail) quiet.push(t.detail);
    const flowLine = !i.batch && t.flow && t.state === 'open'
      ? `flow: ${t.flow.name} · step ${t.flow.step} of ${t.flow.steps}${t.flow.next ? ` · next\u00a0${t.flow.next}` : ''}`
      : null;

    /* CONTEXT — who asked, or what it is waiting on. */
    const ctx = (() => {
      if (t.handedByMe) return `with ${shortName(t.assignee)} · ${clock(t.handedAt)}`;
      if (closed) return t.state === 'cant' ? `came back · ${clock(t.completedAt)}` : `at ${clock(t.completedAt)}`;
      if (i.batch) return req;
      if (t.state === 'in_progress' && t.startedAt) {
        if (left === null) return `started ${clock(t.startedAt)}`;
        return left > 0
          ? `started ${clock(t.startedAt)} · ⏱ ${mmss(left)} running`
          : `started ${clock(t.startedAt)} · ⏱ time’s up`;
      }
      switch (t.kind) {
        case 'vitals':
          return t.appointment
            ? `#${t.appointment.number} · ${t.appointment.status === 'in-progress' ? 'in the room' : 'waiting'}`
            : 'waiting';
        case 'injection':
          return t.orderSignedAt ? `${req} · signed ${clock(t.orderSignedAt)}` : req;
        case 'consent':
          return 'blocks the order';
        case 'chaperone':
          return 'now';
        default:
          if (t.nurseOnly) return 'walk-in · billed as a service';
          return [req, sinceWhen(t.orderSignedAt ?? t.createdAt, now)].filter(Boolean).join(' · ');
      }
    })();

    /* STATE — the one verb this row needs, in place. */
    const state = (() => {
      if (t.handedByMe) return <Chip kind="outline" small>handed over</Chip>;
      if (i.batch) {
        const done = i.tasks.filter((s) => !isOpen(s)).length;
        return (
          <>
            <Chip kind="outline" small>{done} of {i.tasks.length}</Chip>
            <p className="t-cap mut" style={{ marginTop: 6 }}>
              back when {i.tasks.length === 2 ? 'both finish' : 'all finish'}
            </p>
          </>
        );
      }
      if (t.state === 'done') return <Chip kind="success" small>done ✓</Chip>;
      if (t.state === 'cant') return <Chip kind="warn" small>{cantLabel(t.cantReason)}</Chip>;
      if (t.kind === 'observe' && t.state === 'in_progress') {
        return (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <DChip kind="success" onClick={() => act.complete.mutate({ id: t.id, outcome: 'left_well' }, { onError: fail })}>
              Left well
            </DChip>
            <DChip kind="danger" onClick={() => act.complete.mutate({ id: t.id, outcome: 'not_well' }, { onError: fail })}>
              ⚠ Not well
            </DChip>
          </span>
        );
      }
      if (t.state === 'in_progress') {
        return left !== null
          ? <Chip kind="outline" small>⏱ {mmss(left)}</Chip>
          : <DChip kind="soft" onClick={() => act.complete.mutate({ id: t.id }, { onError: fail })}>Done</DChip>;
      }
      switch (t.kind) {
        case 'dilate':
          return (
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <DChip kind="warn" onClick={() => act.start.mutate(t.id, { onError: fail })}>Start</DChip>
              <DChip kind="outline" onClick={() => setCantTask(t)}>Can’t</DChip>
            </span>
          );
        case 'vitals':
          return <DChip kind="warn" onClick={() => navigate(`/nurse/day/vitals/${t.id}`)}>Take</DChip>;
        case 'injection':
          return <DChip kind="soft" onClick={() => navigate(`/nurse/day/task/${t.id}`)}>Give</DChip>;
        case 'consent':
          return (
            <DChip kind="soft" onClick={() => scan(t.id)}>
              <Icon name="camera" size="sm" /> Scan signed
            </DChip>
          );
        case 'chaperone':
          return (
            <DChip kind="soft" onClick={() => act.complete.mutate({ id: t.id, outcome: 'present' }, { onError: fail })}>
              Stamp presence
            </DChip>
          );
        default:
          /* A dressing, a line of teaching: "Do" finishes it — nothing to set. */
          return (
            <DChip kind="soft" onClick={() => act.complete.mutate({ id: t.id, outcome: 'done' }, {
              onSuccess: () => toast.success('Done ✓ — kept on the task with your name'),
              onError: fail,
            })}>
              Do
            </DChip>
          );
      }
    })();

    return {
      id: i.id,
      tone: t.lane === 'rooms' && t.state === 'in_progress' ? 'nested' : undefined,
      cells: {
        lane: showLane ? <Chip kind={lane.kind} small>{lane.label}</Chip> : null,
        task: (
          <>
            <p className="t-strong">{title}{chips ? <> {chips}</> : null}</p>
            {flowLine ? <p className="t-cap brand" style={{ marginTop: 6 }}>{flowLine}</p> : null}
            {flowLine && t.timerSeconds ? (
              <p className="t-cap mut" style={{ marginTop: 6 }}>
                ⏱ {Math.round(t.timerSeconds / 60)} min{t.defaultTimerSeconds && t.defaultTimerSeconds !== t.timerSeconds
                  ? ` · changed from ${Math.round(t.defaultTimerSeconds / 60)}` : ' from the flow'}
              </p>
            ) : null}
            {quiet.length ? <p className="t-cap mut" style={{ marginTop: 6 }}>{quiet.join(' · ')}</p> : null}
          </>
        ),
        ctx: <span className="t-sec mut">{ctx}</span>,
        state,
      },
    } satisfies TableRow;
  });
  const rows = allRows.slice(0, shown);

  /* THE LANES AS HEADINGS ON A PHONE (`g_n1`): the desk's lane column becomes
     a label over the cards it owns, with the board's own wording. */
  const phoneRows: TableRow[] = [];
  {
    let at: string | null = null;
    items.forEach((i, k) => {
      if (i.lane !== at) {
        at = i.lane;
        phoneRows.push({ id: `lane-${i.lane}`, section: LANE_HEAD[i.lane], cells: {} });
      }
      phoneRows.push(allRows[k]);
    });
  }

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: selectedId !== null && !!selected,
      onPaneClose: () => setSelectedId(null),
      paneMenu: (close) => <NurseMenu door="day" onClose={close} />,
      /* `g_n2`/`g_n3`: "‹ My day ▸ Dilate · Mona A." */
      phoneTitle: 'My day',
      paneTitle: selected ? execTitle(selected.primary) : undefined,
      title: `My day · ${branchName}`,
      sub: roomsLine ? (
        <>
          <button type="button" className="lnk" onClick={() => navigate('/queue')}>{roomsLine}</button>
          {' '}— the rooms, live
        </>
      ) : (
        <>No room is in a visit yet — the rooms, live</>
      ),
      search: 'Search my day…',
      onSearch: () => setSearching(true),
      primary: { label: 'Hand to', onClick: () => navigate('/nurse/day/hand-over') },
      pane: <TaskPane itemId={selected?.id ?? null} filter={filter} />,
      /* `g_n1` — the same three lanes, as cards. The rows are the table's own,
         so a lane chip or a countdown can never read differently here. */
      phone: (
        <PhoneScreen
          className="nurse-phone"
          title={`Nurse · ${branchName}`}
          sub={roomsLine ? `${roomsLine} — the rooms, live` : 'No room is in a visit yet'}
          trailing={
            <Chip kind="outline" small className="chip-act w6"
              onClick={() => navigate('/nurse/day/hand-over')}>Hand to</Chip>
          }
          menu={(close) => (
            <NurseMenu door="day" onClose={close} counts={{ day: `${items.length} on your day` }} />
          )}
        >
          <PhoneCards
            columns={COLS}
            rows={phoneRows}
            /* The desk leads with the Lane chip; a card leads with the task and
               wears the lane beside its state. */
            body="task"
            trailing="state"
            hide={['lane']}
            onOpen={(r) => setSelectedId(r.id)}
            empty={<EmptyState title="Nothing on your day yet"
              sub="A doctor's request, a dilation to time or a vitals capture lands here the moment it is made." />}
          />
        </PhoneScreen>
      ),
    },
    [branchName, roomsLine, selected?.id, filter, day.dataUpdatedAt, now, shown, items.length, selectedId],
  );

  /* The sentence counts TASKS ("your nine tasks went"), not rows. */
  const handedTotal = tasks.filter((t) => t.handedByMe).length;
  const loaded = Boolean(day.data);
  const nothingOpen = loaded && counts.open === 0;

  return (
    <div className="maie desk nurse-root">
      <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" hidden
        onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />

      {nothingOpen && filter === 'open' ? null : (
        <div className="filterrow">
          <span className="t-ovl mut" style={{ marginRight: 4 }}>Lanes</span>
          <Chip small kind={filter === 'open' ? 'brand' : 'outline'} className={filter === 'open' ? 'w6' : undefined}
            onClick={() => setFilter('open')}>Open · {counts.open}</Chip>
          <Chip small kind={filter === 'handed' ? 'brand' : 'outline'} className={filter === 'handed' ? 'w6' : undefined}
            onClick={() => setFilter('handed')}>Handed over · {counts.handedOver}</Chip>
          <Chip small kind={filter === 'all' ? 'brand' : 'outline'} className={filter === 'all' ? 'w6' : undefined}
            onClick={() => setFilter('all')}>All · {counts.all}</Chip>
          <span className="t-cap mut" style={{ marginLeft: 4 }}>done rows are hidden until you ask for them</span>
          <span className="grow" />
          <DChip kind="warn" onClick={() => setFlagOpen(true)}>⚠ Flag low stock</DChip>
        </div>
      )}

      <div className="dcontent">
        {!loaded ? (
          <p className="t-sec mut">{day.isError ? 'Your day could not be loaded — it retries on its own.' : 'Loading your day…'}</p>
        ) : nothingOpen && filter === 'open' ? (
          <>
            <div className="emptystate">
              <p className="es-t">Nothing waiting — the rooms will call you.</p>
              <p className="t-sec mut">
                {handedTotal > 0
                  ? `Your ${numberWord(handedTotal)} task${handedTotal === 1 ? '' : 's'} went with the hand-over. A running clock travelled with its task, and the original start and giver stayed on the record.`
                  : 'Nothing has been asked of you yet today. Tasks you finish stay on the record, under All.'}
              </p>
              <p className="mt12">
                <DChip kind="outline" onClick={() => setFilter('all')}>Back to today’s list</DChip>
              </p>
            </div>
            <p className="t-sec mut mt16">
              New work lands here the moment a room calls, a doctor signs a plan, or a walk-in is booked as a
              nurse-only visit.
            </p>
          </>
        ) : (
          <DataTable
            className="nurse-fixed"
            columns={COLS}
            rows={rows}
            selectedId={selected?.id ?? null}
            onSelect={(r) => setSelectedId(r.id)}
            caption="My day"
            total={items.length}
            onLoadMore={() => setShown((n) => n + PAGE)}
            empty={
              filter === 'handed' ? (
                <div className="emptystate">
                  <p className="es-t">Nothing handed over today.</p>
                  <p className="t-sec mut">
                    A task you hand keeps its own clock, with the original start and giver on the record — it would be
                    listed here, with the person who took it.
                  </p>
                  <p className="mt12">
                    <DChip kind="outline" onClick={() => navigate('/nurse/day/hand-over')}>Hand a task over</DChip>
                  </p>
                </div>
              ) : (
                <div className="emptystate">
                  <p className="es-t">Nothing in this list</p>
                  <p className="t-sec mut">New work lands here the moment a room calls or a doctor signs a plan.</p>
                </div>
              )
            }
          />
        )}
      </div>

      {cantTask ? (
        <CantModal task={cantTask} open onClose={() => setCantTask(null)} onDone={() => setCantTask(null)}
          sub={<DayCantSub />} />
      ) : null}
      <FlagLowModal open={flagOpen} onClose={() => setFlagOpen(false)} />
      <NurseSearch open={searching} onClose={() => setSearching(false)} onPick={dock} />
    </div>
  );
}
